/**
 * The editing surface for one document (Documents tab).
 *
 * Composes a two-tier top chrome — Tier 1 document actions (title, autosave
 * indicator, comments toggle, {@link VersionsDropdown}, {@link DownloadMenu},
 * Sync with profile, and Save version) and Tier 2 persistent formatting
 * {@link DocumentRibbon} — driving a continuous canvas of per-section
 * {@link MarkdownEditor}s with active section focus tracking. Also integrates
 * the {@link BlueprintSchemaGuard} (locks structural edits for `blueprint` docs
 * and surfaces the provider-approval gate) and {@link CommentSidebar} inside a
 * unified scroll container with collision-avoidance margin alignment,
 * selection-aligned pending composer positioning, and canvas zoom scaling
 * (50%–200%) with dynamic column layout width preventing margin rail overlap.
 * All clinical content lives in hook state; nothing is logged (HIPAA).
 *
 * When the document was generated from Blueprint cards in any version (the
 * backend-computed `has_card_provenance` flag — not just the current version's
 * `source_card_ids`, which a normal edit-commit drops), a **"Sync with
 * Blueprint"** button is shown alongside the Download button. Clicking it POSTs
 * the sync-back endpoint, which extracts clinical divergences and proposes card
 * updates via the governed ingestion pipeline — reviewed through the full-size
 * {@link ReviewPanel} (opened via {@link ReviewControlContext}).
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  useDocumentVersions,
  type SaveState,
} from "../../hooks/useDocumentVersions";
import { useComments } from "../../hooks/useComments";
import { requiresProviderApproval, toSectionKey } from "./BlueprintSchemaGuard";
import { resolveCommentAnchor } from "./commentAnchor";
import CommentSidebar from "./CommentSidebar";
import CompareVersionsDialog from "./CompareVersionsDialog";
import MarkdownEditor from "./MarkdownEditor";
import type {
  CommentAnchor,
  DocumentComment,
  SourceCitation,
} from "../../types";
import SaveVersionReviewDialog from "./SaveVersionReviewDialog";
import VersionsDropdown from "./VersionsDropdown";
import { useReviewControl } from "../ReviewControlContext";
import { useSyncBack } from "../../hooks/useSyncBack";
import type { VersionDiff } from "../../types";
import { downloadDocument } from "../../utils/download";
import type { DocumentExportFormat } from "../../utils/download";
import DownloadMenu from "./DownloadMenu";
import { DocumentRibbon } from "./DocumentRibbon";
import type { TipTapEditorHandle } from "./TipTapEditor";
import { computeCommentPositions } from "./commentPositioning";
import apiUtils, { isApiError } from "../../apiUtils";

/**
 * Section key shown read-only and collapsed by default in the editor.
 *
 * `metadata` is the blueprint's display-only block (schema version, last
 * updated, connected agents) — it is not an editable card, so in the Document
 * Editor it renders disabled and starts collapsed rather than expanded.
 */
const READ_ONLY_SECTION_KEY = "metadata";

/** Human-readable label for the autosave indicator. */
const SAVE_LABEL: Record<SaveState, string> = {
  idle: "",
  unsaved: "Unsaved changes",
  saving: "Saving…",
  saved: "Draft saved",
};

/** The autosave status indicator. */
function SaveIndicator({ state }: { state: SaveState }): React.ReactElement {
  return (
    <span
      data-testid="save-indicator"
      className={`text-xs font-medium ${
        state === "saved" ? "text-emerald-600" : "text-slate-500"
      }`}
    >
      {SAVE_LABEL[state]}
    </span>
  );
}

/** Props for {@link DocumentEditor}. */
interface DocumentEditorProps {
  /** The document being edited. */
  documentId: string;
}

/** Props for {@link AddSectionBox}. */
interface AddSectionBoxProps {
  /** Existing section keys, to reject duplicates. */
  existingKeys: readonly string[];
  /** Add a new empty section under the derived key. */
  onAdd: (key: string) => void;
}

/** The blueprint "Add section" affordance (title input + button). */
function AddSectionBox({
  existingKeys,
  onAdd,
}: AddSectionBoxProps): React.ReactElement {
  const [title, setTitle] = useState("");
  const key = toSectionKey(title);
  const duplicate = key !== "" && existingKeys.includes(key);
  const submit = (): void => {
    if (key === "" || duplicate) return;
    onAdd(key);
    setTitle("");
  };
  return (
    <div
      data-testid="add-section-box"
      className="rounded-xl border border-dashed border-slate-300 p-3 space-y-2"
    >
      <div className="flex gap-2">
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") submit();
          }}
          placeholder="New section title"
          aria-label="New section title"
          data-testid="add-section-input"
          className="flex-1 border border-slate-300 rounded px-2 py-1 text-sm"
        />
        <button
          type="button"
          data-testid="add-section-button"
          onClick={submit}
          disabled={key === "" || duplicate}
          className="px-3 py-1 bg-slate-800 text-white rounded text-sm hover:bg-slate-700 disabled:opacity-40"
        >
          Add section
        </button>
      </div>
      {duplicate && (
        <p
          data-testid="add-section-duplicate"
          className="text-xs text-rose-700"
        >
          A section named “{key}” already exists.
        </p>
      )}
    </div>
  );
}

/** The diff-history panel state. */
interface DiffSelection {
  fromNo: number | null;
  toNo: number | null;
}

/** Props for {@link SectionPanel}. */
interface SectionPanelProps {
  /** The section key being edited. */
  sectionKey: string;
  /** The section's current markdown value. */
  value: string;
  /** Whether this section is expanded. */
  open: boolean;
  /** Whether a delete control should be offered for this section. */
  deletable: boolean;
  /** Whether this section is read-only (editing disabled). */
  readOnly: boolean;
  /** Toggle this section's expanded state. */
  onToggle: (key: string) => void;
  /** Edit the section's markdown. */
  onEdit: (key: string, markdown: string) => void;
  /** Delete the section. */
  onDelete: (key: string) => void;
  /** `{ source id: title }` for the section's inline citations. */
  sourceTitles?: Record<string, string> | null;
  /** Per-claim provenance for the section's inline citations. */
  citations?: SourceCitation[] | null;
  /** `{ card id: name }` for the section's inference markers. */
  cardTitles?: Record<string, string> | null;
  /** Comments anchored to this section, rendered as inline highlights. */
  comments?: DocumentComment[];
  /** The active comment id (brighter highlight), or null. */
  activeCommentId?: string | null;
  /** The hovered comment id (BLA-318), or null. */
  hoveredCommentId?: string | null;
  /** Called when a comment highlight in this section is clicked. */
  onSelectComment?: (id: string) => void;
  /** Called when a comment highlight in this section is hovered/unhovered. */
  onHoverComment?: (id: string | null) => void;
  /** Called when the provider comments on a selection in this section. */
  onCreateComment?: (anchor: CommentAnchor, clientY?: number) => void;
  /** Optional callback publishing the rich editor's active handle to parent (for ribbon synchronization). */
  onActiveHandle?: (
    handle: import("./TipTapEditor").TipTapEditorHandle | null,
  ) => void;
  /** Optional callback notifying parent when this section gains focus. */
  onFocus?: () => void;
  /** Optional callback notifying parent when this section loses focus. */
  onBlur?: () => void;
}

/**
 * A collapsible editor panel for one document section.
 *
 * Wraps the {@link MarkdownEditor} in an accessible `<details>`/`<summary>`
 * disclosure so a long document can be focused one section at a time. Sections
 * are expanded by default; the parent owns the open state per key. A read-only
 * section (the `metadata` block) disables its editor — `metadata` is not an
 * editable card, so it is shown for reference but never authored here.
 *
 * @param props - The section key, value, open/deletable/readOnly flags, and
 *   handlers.
 * @returns The collapsible section panel element.
 */
function SectionPanel({
  sectionKey,
  value,
  open,
  deletable,
  readOnly,
  onToggle,
  onEdit,
  onDelete,
  sourceTitles,
  citations,
  cardTitles,
  comments,
  activeCommentId,
  hoveredCommentId,
  onSelectComment,
  onHoverComment,
  onCreateComment,
  onActiveHandle,
  onFocus,
  onBlur,
}: SectionPanelProps): React.ReactElement {
  return (
    <details
      open={open}
      data-testid={`section-panel-${sectionKey}`}
      className="rounded-xl border border-slate-200"
    >
      <summary
        data-testid={`section-toggle-${sectionKey}`}
        aria-expanded={open}
        onClick={(event) => {
          // Drive open state ourselves so it stays controlled and testable.
          event.preventDefault();
          onToggle(sectionKey);
        }}
        className="flex items-center justify-between cursor-pointer px-3 py-2 list-none"
      >
        <span className="flex items-center gap-2">
          <span
            aria-hidden="true"
            className={`text-slate-400 transition-transform ${
              open ? "rotate-90" : ""
            }`}
          >
            ▸
          </span>
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            {sectionKey}
          </span>
        </span>
        {deletable && (
          <button
            type="button"
            data-testid={`delete-section-${sectionKey}`}
            onClick={(event) => {
              // Don't toggle the disclosure when deleting.
              event.preventDefault();
              event.stopPropagation();
              onDelete(sectionKey);
            }}
            aria-label={`Delete section ${sectionKey}`}
            className="text-xs font-medium text-rose-600 hover:text-rose-700"
          >
            Delete section
          </button>
        )}
      </summary>
      <div className="border-t border-slate-100 p-3">
        <MarkdownEditor
          sectionKey={sectionKey}
          value={value}
          disabled={readOnly}
          onChange={(markdown) => onEdit(sectionKey, markdown)}
          sourceTitles={sourceTitles}
          citations={citations}
          cardTitles={cardTitles}
          comments={comments}
          activeCommentId={activeCommentId}
          hoveredCommentId={hoveredCommentId}
          onSelectComment={onSelectComment}
          onHoverComment={onHoverComment}
          onCreateComment={onCreateComment}
          onActiveHandle={onActiveHandle}
          onFocus={onFocus}
          onBlur={onBlur}
        />
      </div>
    </details>
  );
}

/**
 * Render the document editor for a single document.
 *
 * @param props - The document id to edit.
 * @returns The editor element.
 */
export const DocumentEditor: React.FC<DocumentEditorProps> = ({
  documentId,
}) => {
  const doc = useDocumentVersions(documentId);
  const reviewControl = useReviewControl();
  const syncBack = useSyncBack(documentId);
  const comments = useComments(documentId);
  const [activeCommentId, setActiveCommentId] = useState<string | null>(null);
  // Hover parity (BLA-318): mirrors activeCommentId but for hover, which is
  // additive to click (unavailable on touch) rather than a replacement.
  const [hoveredCommentId, setHoveredCommentId] = useState<string | null>(null);
  // A span anchor captured from an editor selection, awaiting a body in the
  // sidebar composer. Null when the next comment is document-level.
  const [pendingAnchor, setPendingAnchor] = useState<CommentAnchor | null>(
    null,
  );
  const [pendingTop, setPendingTop] = useState<number | null>(null);
  const [zoom, setZoom] = useState(100);
  const [editorHandles, setEditorHandles] = useState<
    Record<string, TipTapEditorHandle>
  >({});
  const [activeSectionKey, setActiveSectionKey] = useState<string | null>(null);
  const paperRef = useRef<HTMLDivElement>(null);

  const handleRegisterEditor = useCallback(
    (key: string, handle: TipTapEditorHandle | null): void => {
      setEditorHandles((prev) => {
        if (!handle) {
          const next = { ...prev };
          delete next[key];
          return next;
        }
        if (prev[key] === handle) return prev;
        return { ...prev, [key]: handle };
      });
    },
    [],
  );

  const handleSectionFocus = useCallback((key: string): void => {
    setActiveSectionKey(key);
  }, []);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [note, setNote] = useState("");
  const [approved, setApproved] = useState(false);
  const [justCommitted, setJustCommitted] = useState(false);
  // Save-version review dialog (BLA-362/B1): Save no longer commits directly —
  // it opens this review first. `reviewDiff`/`reviewDiffLoading` are the
  // preview diff against the pending content; `summaryLoading` gates only the
  // AI "what changed" prefill (B4 — never blocks the diff or Confirm).
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewDiff, setReviewDiff] = useState<VersionDiff | null>(null);
  const [reviewDiffLoading, setReviewDiffLoading] = useState(false);
  // Whether `note` currently holds an untouched AI draft rather than text the
  // provider wrote. An AI summary describes ONE specific edit, so it must not
  // outlive the review that produced it: carried into a later review it would
  // be persisted as that version's change-note, labelling the audit trail with
  // a change that was never made. A provider-typed note has no such expiry and
  // survives every exit path — so the two are tracked apart rather than both
  // cleared. A single keystroke in the field makes it provider-typed for good.
  const noteIsAiDraftRef = useRef(false);
  // Identifies the review session each in-flight preview/summary belongs to.
  // Opening Save, cancelling, and confirming all bump it, so a response from
  // a superseded session (Save → Cancel → edit → Save again, with the first
  // AI summary still in flight) is dropped instead of describing a different
  // edit than the one the provider is about to confirm and persist as the
  // version note. Mirrors `loadVersionContent`'s call token in the hook.
  const reviewSessionRef = useRef(0);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [noChangeNotice, setNoChangeNotice] = useState(false);
  const [restoreError, setRestoreError] = useState<string | null>(null);
  const [sel, setSel] = useState<DiffSelection>({ fromNo: null, toNo: null });
  const [diff, setDiff] = useState<VersionDiff | null>(null);
  // Whether the on-demand "Compare versions" dialog is open (ADR-062/063 —
  // manual comparison is retained, just relocated off the always-visible panel).
  const [compareOpen, setCompareOpen] = useState(false);
  const [downloadError, setDownloadError] = useState(false);
  const [pulling, setPulling] = useState(false);
  const [pullNotice, setPullNotice] = useState<string | null>(null);
  const [pullError, setPullError] = useState<string | null>(null);
  // Collapsed section keys. Content sections are expanded by default, so only
  // the explicitly-collapsed keys are tracked (a key absent here renders open).
  // The read-only `metadata` block is the exception: it starts collapsed (it is
  // reference-only, not an editable card), so seed it lazily when present.
  const [collapsed, setCollapsed] = useState<Set<string>>(() =>
    READ_ONLY_SECTION_KEY in doc.content
      ? new Set([READ_ONLY_SECTION_KEY])
      : new Set(),
  );

  const toggleSection = (key: string): void => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const [commentPositions, setCommentPositions] = useState<Map<string, number>>(
    () => new Map(),
  );

  useEffect(() => {
    if (!commentsOpen || (comments.comments.length === 0 && !pendingAnchor)) {
      /* eslint-disable-next-line react-hooks/set-state-in-effect */
      setCommentPositions((prev) => (prev.size === 0 ? prev : new Map()));
      return;
    }

    const measure = (): void => {
      const paperRect = paperRef.current?.getBoundingClientRect();

      const targets: { id: string; idealTop: number; height: number }[] = [];
      if (pendingAnchor && pendingTop !== null) {
        targets.push({ id: "__pending__", idealTop: pendingTop, height: 220 });
      }
      for (const c of comments.comments) {
        const el = document.querySelector(
          `[data-comment-id="${c.id}"]`,
        ) as HTMLElement | null;

        if (el && paperRect) {
          const elRect = el.getBoundingClientRect();
          const idealTop = Math.max(0, elRect.top - paperRect.top);
          targets.push({ id: c.id, idealTop, height: 160 });
        }
      }

      const nextPositions = computeCommentPositions(targets);
      setCommentPositions((prev) => {
        if (prev.size !== nextPositions.size) return nextPositions;
        for (const [id, top] of nextPositions) {
          if (prev.get(id) !== top) return nextPositions;
        }
        return prev;
      });
    };

    measure();

    let observer: MutationObserver | null = null;
    if (typeof MutationObserver !== "undefined" && paperRef.current) {
      observer = new MutationObserver(() => {
        measure();
      });
      observer.observe(paperRef.current, { childList: true, subtree: true });
    }

    window.addEventListener("resize", measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [
    commentsOpen,
    comments.comments,
    collapsed,
    pendingAnchor,
    pendingTop,
    zoom,
  ]);

  if (doc.status === "loading") {
    return <p className="text-sm text-slate-500">Loading document…</p>;
  }
  if (doc.status === "error" || doc.document === null) {
    return <p className="text-sm text-rose-600">Unable to load document.</p>;
  }

  const committed = doc.version?.content ?? {};
  const locked = doc.document.type === "blueprint";
  // Compare against the content a commit would actually persist (editor content
  // overlaid onto the committed version), so an ordinary content edit — which
  // leaves the committed section *set* intact, including non-card sections like
  // the blueprint `metadata` block — does not spuriously require approval.
  const needsApproval = requiresProviderApproval(
    doc.document.type,
    committed,
    doc.commitContent,
  );

  // BLA-362/B1: "Save version" no longer commits directly — it opens the
  // review dialog. The no-change case is handled honestly BEFORE the dialog
  // (and before any network call): comparing the exact content a commit
  // would persist against what is already committed, the same comparison the
  // backend's diff_preview would make, so there is never a reason to show an
  // empty diff and ask for confirmation.
  const contentUnchanged = (
    committedContent: Record<string, string>,
    nextContent: Record<string, string>,
  ): boolean => {
    const committedKeys = Object.keys(committedContent);
    const nextKeys = Object.keys(nextContent);
    // A key present on only one side is a structural change (e.g. adding an
    // empty section) even when its value happens to compare equal to the
    // shared "absent" default — key PRESENCE must be compared, not just the
    // value each side happens to hold.
    if (committedKeys.length !== nextKeys.length) return false;
    for (const key of nextKeys) {
      if (!(key in committedContent)) return false;
      if (committedContent[key] !== nextContent[key]) return false;
    }
    return true;
  };

  const handleCommit = (): void => {
    if (contentUnchanged(committed, doc.commitContent)) {
      setNoChangeNotice(true);
      return;
    }
    setNoChangeNotice(false);
    setJustCommitted(false);
    // An AI draft belongs to the review that produced it. Expiring it here —
    // at the single entry point every review session passes through — covers
    // all three ways the previous session could have ended (cancel, a
    // successful commit, and a conflict/error that closed the dialog with the
    // draft intact), so a summary describing an abandoned edit can never
    // become the note on a later, different commit.
    if (noteIsAiDraftRef.current) {
      noteIsAiDraftRef.current = false;
      setNote("");
    }
    setReviewOpen(true);
    setReviewDiff(null);
    setReviewDiffLoading(true);
    setSummaryLoading(true);
    const session = ++reviewSessionRef.current;
    const pendingContent = doc.commitContent;
    // B1: the diff must show immediately — this is the only awaited call
    // gating what the dialog displays.
    void doc.loadDiffPreview(pendingContent).then((result) => {
      if (reviewSessionRef.current !== session) return;
      setReviewDiff(result);
      setReviewDiffLoading(false);
    });
    // B3/B4: the AI summary is a non-blocking prefill that populates
    // whenever it arrives — it never gates opening the dialog or the diff
    // above, and a failure (fail open + quiet) simply leaves the field
    // empty. "Prefill, never replace": only applied if the note is still
    // untouched when the summary lands.
    void doc.loadChangeSummary(pendingContent).then((summary) => {
      if (reviewSessionRef.current !== session) return;
      setSummaryLoading(false);
      if (summary === "") return;
      setNote((prev) => {
        if (prev !== "") return prev;
        noteIsAiDraftRef.current = true;
        return summary;
      });
    });
  };

  const handleNoteChange = (next: string): void => {
    noteIsAiDraftRef.current = false;
    setNote(next);
  };

  const handleConfirmSave = (): void => {
    const session = ++reviewSessionRef.current;
    setCommitting(true);
    void doc.commit(note, approved).then((result) => {
      // `committing` tracks "a write is in flight" for the whole editor, so
      // it always clears; the dialog lifecycle and the note belong to the
      // session that started this commit and must not be touched once a
      // newer review has claimed them.
      setCommitting(false);
      if (reviewSessionRef.current !== session) return;
      setReviewOpen(false);
      if (result === "ok") {
        setNote("");
        noteIsAiDraftRef.current = false;
        setApproved(false);
        setJustCommitted(true);
      }
      // A conflict/error closes the dialog so the existing conflict-prompt /
      // commit-error banners (in the action bar below) surface the failure —
      // the note/approval the provider entered are preserved, so reopening
      // Save-version resumes with them intact.
    });
  };

  // Cancel returns to editing with edits intact — never discards work. This
  // dialog never touched `doc.content`/`doc.commitContent` (it only reads
  // them), so the editor's live content is untouched by construction; only
  // the review dialog's own transient preview state is cleared. An untouched
  // AI draft note does not survive into the next review either, but that is
  // handled once at `handleCommit` rather than per exit path.
  const handleCancelSave = (): void => {
    reviewSessionRef.current += 1;
    setReviewOpen(false);
    setReviewDiff(null);
    setReviewDiffLoading(false);
    setSummaryLoading(false);
  };

  // One-click restore of a committed version. On success the restored content
  // is loaded + committed and the editor reloads, so the pane visibly changes to
  // the restored content — that IS the confirmation (a success banner would be
  // cleared by the post-reload editor remount, the same pre-existing quirk that
  // already hides the commit-success banner; a failure surfaces below instead).
  // A section-reshaping Blueprint restore comes back as "approval" (the backend
  // schema-lock 422): confirm the structural change with the provider, then
  // retry once with approval — never silently, and never a loop (an approved
  // retry cannot 422 again).
  const handleRestore = (versionNo: number): void => {
    setRestoreError(null);
    void doc.restore(versionNo, false).then((result) => {
      if (result === "ok") return;
      if (result !== "approval") {
        setRestoreError("Could not restore this version. Please try again.");
        return;
      }
      const approvedRestore = window.confirm(
        `Restoring version ${versionNo} changes this Blueprint's sections. ` +
          "Approve the section-schema change and restore it as the current version?",
      );
      if (!approvedRestore) return;
      void doc.restore(versionNo, true).then((retry) => {
        if (retry !== "ok") {
          setRestoreError("Could not restore this version. Please try again.");
        }
      });
    });
  };

  const handleDownload = (format: DocumentExportFormat): void => {
    setDownloadError(false);
    void downloadDocument(documentId, format).catch(() =>
      setDownloadError(true),
    );
  };

  const handlePullProfile = async (): Promise<void> => {
    setPulling(true);
    setPullNotice(null);
    setPullError(null);
    try {
      const res = await apiUtils.fetchData<{
        status: string;
        updated: boolean;
        document_id: string;
        upstream_version: number;
        version_no: number | null;
        note: string | null;
      }>(
        `/api/documents/${encodeURIComponent(documentId)}/pull-profile`,
        "POST",
      );
      if (isApiError(res)) {
        setPullError(res.error || "Failed to pull profile");
      } else if (res.updated) {
        setPullNotice(res.note || "Updated profile version");
        doc.reload();
      } else {
        setPullNotice("Profile is up to date.");
      }
    } catch (err) {
      setPullError(
        err instanceof Error ? err.message : "Failed to pull profile",
      );
    } finally {
      setPulling(false);
    }
  };

  const handleSyncBack = (): void => {
    void syncBack.syncBack().then((run) => {
      if (run !== null) {
        reviewControl.openReview(run);
      }
    });
  };

  // "Sync with profile" is offered for documents generated from cards. The
  // backend-computed `hasCardProvenance` is true when ANY version recorded the
  // grounding `source_card_ids` — not just the current one. A normal edit-commit
  // drops `source_card_ids` from the current version, so gating on the current
  // version alone would hide the button right after the provider edits the
  // derivative (the primary sync-back flow); the any-version flag mirrors the
  // backend's provenance lookup so the button stays available.
  const isGeneratedFromCards = doc.hasCardProvenance;

  const handleEdit = (key: string, markdown: string): void => {
    if (justCommitted) setJustCommitted(false);
    if (noChangeNotice) setNoChangeNotice(false);
    doc.editSection(key, markdown);
  };

  const handleAddSection = (key: string): void => {
    if (justCommitted) setJustCommitted(false);
    if (noChangeNotice) setNoChangeNotice(false);
    // Insert a new, empty editable section into the working content. This
    // changes the section *set*, which BlueprintSchemaGuard detects as a
    // structural change → the existing provider-approval gate applies.
    doc.editSection(key, "");
  };

  const handleDeleteSection = (key: string): void => {
    // Deleting a section removes it from the section *set* — a structural
    // change BlueprintSchemaGuard detects → the same provider-approval gate
    // add-section uses applies. Confirm first (a destructive, structural act).
    if (!window.confirm(`Delete the "${key}" section from this Blueprint?`)) {
      return;
    }
    if (justCommitted) setJustCommitted(false);
    if (noChangeNotice) setNoChangeNotice(false);
    doc.deleteSection(key);
  };

  const handleSelectVersion = (versionNo: number): void => {
    const next: DiffSelection =
      sel.fromNo === null || sel.toNo !== null
        ? { fromNo: versionNo, toNo: null }
        : { fromNo: sel.fromNo, toNo: versionNo };
    setSel(next);
    if (next.fromNo !== null && next.toNo !== null) {
      const [lo, hi] = [next.fromNo, next.toNo].sort((a, b) => a - b);
      void doc.loadDiff(lo, hi).then(setDiff);
    } else {
      setDiff(null);
    }
  };

  const handleLoadVersion = (versionNo: number): void => {
    if (justCommitted) setJustCommitted(false);
    if (noChangeNotice) setNoChangeNotice(false);
    void doc.loadVersionContent(versionNo);
  };

  const sectionKeys = Object.keys(doc.content);
  const effectiveActiveKey =
    activeSectionKey && sectionKeys.includes(activeSectionKey)
      ? activeSectionKey
      : (sectionKeys[0] ?? null);
  const activeEditorHandle = effectiveActiveKey
    ? (editorHandles[effectiveActiveKey] ?? null)
    : null;

  // Comments anchored to a given section (document-level comments — no
  // section_key — are shown only in the sidebar, never decorated in a section).
  const commentsForSection = (key: string): DocumentComment[] =>
    comments.comments.filter((c) => c.anchor?.section_key === key);

  // A comment is "stale" when its anchored text can no longer be located in its
  // section's current body — the fail-loud signal the sidebar surfaces so a
  // moved/deleted span is never silently mis-highlighted.
  const staleIds = new Set<string>();
  for (const c of comments.comments) {
    const key = c.anchor?.section_key ?? "";
    const body = key in doc.content ? doc.content[key] : "";
    if (resolveCommentAnchor(c.anchor, body).status === "unlocatable") {
      staleIds.add(c.id);
    }
  }

  const handleCommentOnSelection = (
    anchor: CommentAnchor,
    clientY?: number,
  ): void => {
    setPendingAnchor(anchor);
    setCommentsOpen(true);
    if (clientY !== undefined && paperRef.current) {
      const paperRect = paperRef.current.getBoundingClientRect();
      const idealTop = Math.max(0, clientY - paperRect.top);
      setPendingTop(idealTop);
    } else {
      setPendingTop(null);
    }
  };

  const handleSelectComment = (id: string): void => {
    setActiveCommentId(id);
    setCommentsOpen(true);
    if (typeof document !== "undefined") {
      const el = document.querySelector(`[data-comment-id="${id}"]`);
      el?.scrollIntoView?.({ behavior: "smooth", block: "center" });
      const cardEl = document.querySelector(
        `[data-testid="comment-item-${id}"]`,
      );
      cardEl?.scrollIntoView?.({ behavior: "smooth", block: "nearest" });
    }
  };

  // ADR-072: the compose/edit flow no longer collects a "Why?" rationale.
  // A new comment is created with rationale=null; editing a comment's body
  // never touches its rationale field (the update omits the key, and
  // `useComments.updateComment` leaves an absent field untouched), so any
  // rationale already stored on an existing comment (from before this
  // change) is preserved, not destroyed.
  const handleCreateComment = async (body: string): Promise<void> => {
    const result = await comments.createComment({
      body,
      rationale: null,
      anchor: pendingAnchor,
      documentVersionId: doc.version?.id ?? null,
    });
    if (result !== null) {
      setPendingAnchor(null);
      setPendingTop(null);
    } else {
      throw new Error("Failed to create comment");
    }
  };

  const handleUpdateComment = async (
    id: string,
    body: string,
  ): Promise<void> => {
    const result = await comments.updateComment(id, { body });
    if (result === null) {
      throw new Error("Failed to update comment");
    }
  };

  const handleDeleteComment = (id: string): void => {
    void comments.deleteComment(id);
    if (activeCommentId === id) setActiveCommentId(null);
  };

  return (
    <div className="flex h-full flex-col overflow-hidden bg-slate-100">
      {/*
       * Tier 1: Document Top Navigation & Action Bar
       * Unifies document title, autosave indicator, comments toggle, versions flyout,
       * download export menu, sync with blueprint, and primary Save Version button.
       */}
      <div className="bg-white border-b border-slate-200 px-4 py-2 flex flex-wrap items-center justify-between gap-3 shrink-0 relative z-30">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-8 h-8 rounded bg-blue-600 text-white flex items-center justify-center font-bold text-xs shadow-xs shrink-0">
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          </div>
          <div className="flex flex-col min-w-0">
            <h2 className="font-prata font-normal text-lg sm:text-xl text-slate-800 truncate">
              {doc.document.title}
            </h2>
            <div className="flex items-center gap-2 text-xs">
              {doc.isRefreshing ? (
                <span
                  data-testid="refresh-indicator"
                  className="text-xs font-medium text-slate-500"
                >
                  Syncing…
                </span>
              ) : (
                <SaveIndicator state={doc.saveState} />
              )}
            </div>
          </div>
        </div>

        {/* Action Cluster */}
        <div className="flex items-center gap-2 shrink-0">
          {downloadError && (
            <span
              data-testid="download-error"
              className="text-xs font-medium text-rose-600"
            >
              Download failed.
            </span>
          )}
          {syncBack.syncError !== null && (
            <span
              data-testid="sync-back-error"
              className="text-xs font-medium text-rose-600"
            >
              {syncBack.syncError}
            </span>
          )}

          {/* Toggle Comments Drawer */}
          <button
            type="button"
            data-testid="btn-toggle-comments"
            onClick={() => setCommentsOpen((prev) => !prev)}
            className={`h-8 px-3 rounded-md text-xs font-medium border flex items-center gap-1.5 transition-colors ${
              commentsOpen
                ? "bg-amber-50 border-amber-300 text-amber-900"
                : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"
            }`}
            title="Toggle Comments Rail"
            aria-label="Toggle comments"
          >
            <svg
              className="w-4.5 h-4.5 text-amber-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
              />
            </svg>
            Comments
            {comments.comments.length > 0 && (
              <span className="px-1.5 py-0.2 rounded-full bg-amber-100 text-amber-800 text-[10px] font-bold">
                {comments.comments.length}
              </span>
            )}
          </button>

          <VersionsDropdown
            versions={doc.versions}
            fromNo={sel.fromNo}
            toNo={sel.toNo}
            onSelect={handleSelectVersion}
            onLoad={handleLoadVersion}
            onRestore={handleRestore}
            currentVersionNo={doc.version?.version_no ?? null}
            restoreError={restoreError}
            compareReady={diff !== null}
            onOpenCompare={() => setCompareOpen(true)}
          />

          <DownloadMenu onDownload={handleDownload} />

          {/* Pull Latest Profile Button (visible when doc.is_profile is true) */}
          {Boolean(doc.document?.is_profile) && (
            <button
              type="button"
              data-testid="pull-latest-profile"
              onClick={handlePullProfile}
              disabled={pulling}
              title="Pull latest profile & resolve citations"
              aria-label="Pull latest profile & resolve citations"
              className="h-8 w-8 border border-slate-300 text-slate-700 rounded-md flex items-center justify-center hover:bg-slate-50 disabled:opacity-40"
            >
              <svg
                className={`w-4 h-4 ${pulling ? "animate-spin text-blue-600" : ""}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            </button>
          )}

          {isGeneratedFromCards && (
            <button
              type="button"
              data-testid="sync-with-blueprint"
              onClick={handleSyncBack}
              disabled={syncBack.syncing}
              className="h-8 px-3 border border-blue-300 text-blue-700 rounded-md text-xs font-medium flex items-center hover:bg-blue-50 disabled:opacity-40"
            >
              {syncBack.syncing ? "Syncing…" : "Sync with profile"}
            </button>
          )}

          {/* Primary Save Version Button */}
          <button
            type="button"
            data-testid="save-version"
            onClick={handleCommit}
            title="Save version"
            aria-label="Save version"
            className="h-8 w-8 bg-blue-600 text-white rounded-md flex items-center justify-center hover:bg-blue-700 shadow-xs disabled:opacity-40"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4"
              />
            </svg>
          </button>
        </div>
      </div>

      {/*
       * Tier 2: Persistent Formatting Ribbon
       */}
      <DocumentRibbon
        editorHandle={activeEditorHandle}
        disabled={doc.status !== "loaded"}
        zoom={zoom}
        onZoomChange={setZoom}
      />

      {/*
       * Action & Status Banners
       * Preserves all testids: conflict-prompt, draft-conflict-notice,
       * commit-error, commit-success, no-change-notice, commit-action-bar.
       */}
      <div
        data-testid="commit-action-bar"
        className="empty:hidden px-6 pt-3 space-y-2 shrink-0"
      >
        {doc.conflict && (
          <div
            data-testid="conflict-prompt"
            className="rounded-lg bg-rose-50 border border-rose-200 px-3 py-2 text-sm text-rose-800"
          >
            This document changed since you started editing. Reload to rebase
            your changes onto the latest version before committing.
            <button
              type="button"
              onClick={doc.reload}
              className="ml-2 underline font-medium"
            >
              Reload
            </button>
          </div>
        )}
        {doc.draftConflict && (
          <div
            data-testid="draft-conflict-notice"
            className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-800"
          >
            Preserved your autosaved draft (based on version{" "}
            {doc.draftBaseVersion ?? "earlier"}). A newer version was committed
            by another provider.
          </div>
        )}
        {doc.commitError !== null && (
          <p
            data-testid="commit-error"
            className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded px-2.5 py-1.5"
          >
            Could not save this version. {doc.commitError}
          </p>
        )}
        {justCommitted && doc.version !== null && (
          <p
            data-testid="commit-success"
            className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2.5 py-1.5"
          >
            Saved version {doc.version.version_no}.
          </p>
        )}
        {noChangeNotice && (
          <p
            data-testid="no-change-notice"
            className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded px-2.5 py-1.5"
          >
            Nothing changed — nothing to save.
          </p>
        )}
        {pullNotice && (
          <p
            data-testid="pull-profile-notice"
            className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded px-2.5 py-1.5"
          >
            {pullNotice}
          </p>
        )}
        {pullError && (
          <p
            data-testid="pull-profile-error"
            className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded px-2.5 py-1.5"
          >
            {pullError}
          </p>
        )}
      </div>

      {/*
       * Canvas & Collapsible Comments Container (Single Unified Scroll Container)
       */}
      <div className="flex-1 overflow-auto p-4 sm:p-6 min-h-0">
        <div
          className={`mx-auto flex justify-center items-start gap-6 ${
            zoom > 100 ? "w-fit min-w-full" : "max-w-[1240px]"
          }`}
        >
          {/* Main Paper Canvas Column */}
          <div
            data-testid="canvas-column-wrapper"
            className="shrink-0 flex justify-center"
            style={{
              width: zoom > 100 ? `${816 * (zoom / 100)}px` : "816px",
            }}
          >
            {/* Main Paper Canvas */}
            <div
              className="w-[816px] max-w-[816px] space-y-4 shrink-0 transition-transform origin-top"
              style={
                zoom !== 100
                  ? {
                      transform: `scale(${zoom / 100})`,
                      transformOrigin: "top center",
                    }
                  : undefined
              }
            >
              {locked && (
                <div
                  data-testid="schema-guard-note"
                  className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800"
                >
                  This is a Blueprint document. Its section schema is locked —
                  content within each section is free-form, but adding or
                  removing a section requires provider approval.
                </div>
              )}

              {/* Continuous Paper Page */}
              <div
                ref={paperRef}
                data-testid="paper-canvas"
                className="bg-white rounded-lg shadow-sm border border-slate-200/80 p-8 sm:p-10 min-h-[1056px] space-y-6"
              >
                {locked
                  ? sectionKeys.map((key) => {
                      const deletable =
                        key !== READ_ONLY_SECTION_KEY && sectionKeys.length > 1;
                      const readOnly = key === READ_ONLY_SECTION_KEY;
                      return (
                        <SectionPanel
                          key={key}
                          sectionKey={key}
                          value={doc.content[key]}
                          open={!collapsed.has(key)}
                          deletable={deletable}
                          readOnly={readOnly}
                          onToggle={toggleSection}
                          onEdit={handleEdit}
                          onDelete={handleDeleteSection}
                          sourceTitles={doc.sourceTitles}
                          citations={doc.version?.citations}
                          cardTitles={doc.cardTitles}
                          comments={commentsForSection(key)}
                          activeCommentId={activeCommentId}
                          hoveredCommentId={hoveredCommentId}
                          onSelectComment={handleSelectComment}
                          onHoverComment={setHoveredCommentId}
                          onCreateComment={handleCommentOnSelection}
                          onActiveHandle={(handle) =>
                            handleRegisterEditor(key, handle)
                          }
                          onFocus={() => handleSectionFocus(key)}
                        />
                      );
                    })
                  : sectionKeys.map((key) => (
                      <MarkdownEditor
                        key={key}
                        sectionKey={key}
                        value={doc.content[key]}
                        onChange={(markdown) => handleEdit(key, markdown)}
                        sourceTitles={doc.sourceTitles}
                        citations={doc.version?.citations}
                        cardTitles={doc.cardTitles}
                        comments={commentsForSection(key)}
                        activeCommentId={activeCommentId}
                        hoveredCommentId={hoveredCommentId}
                        onSelectComment={handleSelectComment}
                        onHoverComment={setHoveredCommentId}
                        onCreateComment={handleCommentOnSelection}
                        onActiveHandle={(handle) =>
                          handleRegisterEditor(key, handle)
                        }
                        onFocus={() => handleSectionFocus(key)}
                      />
                    ))}

                {locked && (
                  <AddSectionBox
                    existingKeys={sectionKeys}
                    onAdd={handleAddSection}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Unified Inline Margin Comments Rail */}
          <aside
            data-testid="comments-margin-rail"
            className={`w-80 shrink-0 flex flex-col space-y-3 transition-all duration-200 ${
              commentsOpen ? "" : "hidden"
            }`}
          >
            <div className="p-2.5 bg-white border border-slate-200 rounded-lg shadow-2xs flex items-center justify-between">
              <span className="font-bold text-xs text-slate-800 flex items-center gap-1.5">
                <svg
                  className="w-3.5 h-3.5 text-amber-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"
                  />
                </svg>
                Comments ({comments.comments.length})
              </span>
              <button
                type="button"
                onClick={() => setCommentsOpen(false)}
                className="text-slate-400 hover:text-slate-700 text-xs px-1.5 py-0.5 rounded hover:bg-slate-100"
                aria-label="Close comments"
              >
                ✕
              </button>
            </div>
            <CommentSidebar
              comments={comments.comments}
              loading={comments.loading}
              error={comments.error}
              staleIds={staleIds}
              activeCommentId={activeCommentId}
              hoveredCommentId={hoveredCommentId}
              positions={commentPositions}
              onSelect={handleSelectComment}
              onHover={setHoveredCommentId}
              onCreate={handleCreateComment}
              onUpdate={handleUpdateComment}
              onDelete={handleDeleteComment}
              pendingAnchor={pendingAnchor}
              pendingTop={pendingTop}
              onClearPending={() => {
                setPendingAnchor(null);
                setPendingTop(null);
              }}
            />
          </aside>
        </div>
      </div>

      {compareOpen && (
        <CompareVersionsDialog
          diff={diff}
          sourceTitles={doc.sourceTitles}
          onClose={() => setCompareOpen(false)}
        />
      )}

      {reviewOpen && (
        <SaveVersionReviewDialog
          diff={reviewDiff}
          diffLoading={reviewDiffLoading}
          sourceTitles={doc.sourceTitles}
          note={note}
          onNoteChange={handleNoteChange}
          summaryLoading={summaryLoading}
          needsApproval={needsApproval}
          approved={approved}
          onApprovedChange={setApproved}
          onConfirm={handleConfirmSave}
          onCancel={handleCancelSave}
          committing={committing}
        />
      )}
    </div>
  );
};

export default DocumentEditor;
